import React, { useMemo } from "react";
import { Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { PaymentInputs } from "./BookNowPublicModal";

const PLATFORM_FEE_RATE = 0.15;

function dollars(n) {
  const x = Number.isFinite(+n) ? +n : 0;
  return `$${x.toFixed(2)}`;
}

export default function BookNowPreviewModal({
  open,            // activity object or null
  onClose,         // () => void
  creatorName,     // e.g., "Sarah"
  avatarUrl        // string
}) {
  const a = open || {};
  const title = a.title || "Activity";
  const price = Number(a.price || 0);
  const durationLabel = a.durationLabel || "—";
  const location = a.address || "—";
  const description = a.description || a.fullDescription || "";
  const extraCostType = a.extraCostType === "paid_separately" ? "paid_separately" : "included";
  const extraCostNote = a.extraCostNote || "";

  const fee = useMemo(() => Math.round(price * PLATFORM_FEE_RATE * 100) / 100, [price]);
  const total = useMemo(() => Math.round((price + fee) * 100) / 100, [price, fee]);

  // disabled preview values
  // Match public modal date formatting and avoid native date input quirks
  const previewDateLabel = useMemo(() => (
    new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
  ), []);
  const previewTime = "02:00PM"; // visual only (text input)

  const firstName = (creatorName || "").trim().split(" ").filter(Boolean)[0] || "They";

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="sm">
      <div className="pv-modal pv-book">
        {/* Header */}
        <div className="pv-modal-header">
          <IconButton onClick={onClose} className="pv-modal-close" aria-label="Close preview">
            <CloseIcon className="pv-modal-close-icon" />
          </IconButton>
          <h2 className="pv-bn-title">Book an Activity With {creatorName || "Your Buddy"}</h2>
          <div className="pv-bn-sub">(This is a preview)</div>
        </div>

        {/* Activity summary */}
        <div className="pv-bn-section pv-bn-activity">
          <div className="pv-bn-avatar-wrap">
            {avatarUrl ? (
              <img className="pv-bn-avatar" src={avatarUrl} alt={`${creatorName} avatar`} />
            ) : (
              <div className="pv-bn-avatar pv-bn-avatar-fallback">🙂</div>
            )}
          </div>

          <div className="pv-bn-divider" />
          <div className="pv-bn-subtitle pv-bn-activity-info">Activity Info</div>

          <div className="pv-bn-fieldset">
            <div className="pv-bn-label">Name</div>
            <div className="pv-bn-value">{title}</div>
          </div>

          <div className="pv-bn-fieldset">
            <div className="pv-bn-label">Description</div>
            <div className="pv-bn-value pv-bn-paragraph">
              {description || "No description provided."}
            </div>
          </div>

          <div className="pv-bn-fieldset">
            <div className="pv-bn-label">Location</div>
            <div className="pv-bn-value">{location}</div>
          </div>

          <div className="pv-bn-fieldset">
            <div className="pv-bn-label">Duration</div>
            <div className="pv-bn-value">{durationLabel}</div>
          </div>
        </div>

        {/* Date & time (disabled) */}
        <div className="pv-bn-section">
          <div className="pv-bn-divider" />
          <div className="pv-bn-subtitle">Date & Time</div>

          <div className="pv-bn-grid pv-bn-narrow">
            <div className="pv-bn-grid-field">
              <label className="pv-bn-input-label">Date</label>
              <div className="pv-bn-input-wrap">
                <input
                  className="pv-bn-input pv-bn-input--white"
                  type="text"
                  value={previewDateLabel}
                  disabled
                />
                <CalendarMonthIcon className="pv-bn-input-icon" />
              </div>
            </div>

            <div className="pv-bn-grid-field">
              <label className="pv-bn-input-label">Time</label>
              <div className="pv-bn-input-wrap">
                {/* text input to show 02:00PM visual */}
                <input
                  className="pv-bn-input pv-bn-input--white"
                  type="text"
                  value={previewTime}
                  disabled
                />
                <ArrowDropDownIcon className="pv-bn-input-icon" />
              </div>
            </div>
          </div>
        </div>

        {/* Total */}
        <div className="pv-bn-section">
          <div className="pv-bn-divider" />
          <div className="pv-bn-subtitle">Total</div>

          <div className="pv-bn-totals pv-bn-narrow">
            <div className="pv-bn-row">
              <span>Base Price</span>
              <span>{dollars(price)}</span>
            </div>
            <div className="pv-bn-row">
              <span>Platform Fee</span>
              <span>{dollars(fee)}</span>
            </div>
            <div className="pv-bn-row pv-bn-row-strong">
              <span>Total</span>
              <span>{dollars(total)}{extraCostType === "paid_separately" ? " + activity cost" : ""}</span>
            </div>
          </div>

          {/* Extra cost explainer — now centered */}
          <div className="pv-bn-narrow pv-extra-cost">
            {extraCostType === "included" && (
              <>
                <div className="pv-bn-subtitle">Activity Cost: Included</div>
                <div className="pv-bn-note">
                  The cost of the activity is already included in the total.
                </div>
              </>
            )}

            {extraCostType === "paid_separately" && (
              <>
                <div className="pv-bn-subtitle">Activity Cost: Paid Separately</div>
                <div className="pv-bn-note">
                  {firstName} left the following note:
                </div>
                <div className="pv-bn-note pv-bn-note-box">
                  {extraCostNote || "Additional on-site cost paid directly at the activity."}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Your Info (preview only, disabled) */}
        <div className="pv-bn-section pv-yourinfo">
          <div className="pv-bn-divider" />
          <div className="pv-bn-subtitle">Your Info</div>
          <div className="pv-bn-note">Provide your information so the host knows who they’re meeting.</div>
          <div className="pv-bn-narrow">
            <div className="pv-bn-grid pv-bn-grid-single">
              <input className="pv-bn-input pv-bn-input--underline pv-bn-center-ph" placeholder="First Name" disabled />
              <input className="pv-bn-input pv-bn-input--underline pv-bn-center-ph" placeholder="Last Name" disabled />
              <input className="pv-bn-input pv-bn-input--underline pv-bn-center-ph" placeholder="Email" disabled />

            </div>
            <div className="pv-bn-textarea-wrap">
              <textarea
                className="pv-bn-input pv-bn-textarea pv-bn-textarea-box"
                placeholder="Intro Message (Optional)"
                maxLength={150}
                disabled
                readOnly
              />
              <span className="pv-bn-counter">0/150</span>
            </div>
          </div>
        </div>

        {/* Payment (disabled preview look) */}
        <div className="pv-bn-section pv-yourinfo">
          <div className="pv-bn-divider" />
          <div className="pv-bn-subtitle">Payment</div>
          <div className="pv-bn-note pv-bn-note-stripe">🔒 Payments are processed securely via Stripe.</div>

          <div className="pv-bn-narrow">
            <PaymentInputs preview />
          </div>
        </div>

        <div className="pv-bn-actions">
          <div className="pv-bn-note pv-bn-note-small">
            We place a temporary hold on your card that&apos;s only charged if the host accepts your booking.
          </div>
          <button className="pv-bn-btn pv-bn-btn-disabled" disabled>
            Book Now
          </button>
        </div>
      </div>
    </Dialog>
  );
}
